import {
  useTheme,
} from '@mui/material';
import Divider from '@mui/material/Divider';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import FormControlLabel from '@mui/material/FormControlLabel';
import Box from '@mui/material/Box';
import { FormProvider, useForm } from 'react-hook-form';
import { useMemo, useState } from 'react';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import Stack from '@mui/material/Stack';
import dayjs, { Dayjs } from 'dayjs';
import Grid from '@mui/material/Grid2';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { BaseDrawer, BaseDrawerTitle, BaseLabel } from '@mgm/common/components/base';
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';
import { getIsOpenDayOffNav } from '../../../../store/schedules/selectors';
import { schedulesActions } from '../../../../store/schedules/slice';
import { TCreateEditForm } from '../../../../components/form';
import { MultipleDaySelectionPicker } from '../../../../components/pickers';
import { useRequestDayOffAPI } from '../../../../hooks/api/useSchedules';
import { commonActions } from '../../../../store/common/slice';

const DayOffNav = () => {
  const { mutateAsync, isLoading } = useRequestDayOffAPI();
  const isOpenDayOffNav = useAppSelector(getIsOpenDayOffNav);
  const dispatch = useAppDispatch();
  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);
  const { t } = useTranslation('schedule');

  const DAY_OFF_OPTIONS = [
    {
      text: t('vacation'), value: 'Vacation',
    }, {
      text: t('sickLeave'), value: 'Sick leave',
    }, {
      text: t('other'), value: 'Other',
    }];

  const [dates, setDates] = useState<Array<string>>([]);
  const [dayOffData, setDayOffData] = useState<Array<{
    date: string,
    type: string
  }>>([]);
  const [note, setNote] = useState('');

  const methods = useForm<TCreateEditForm>({
    mode: 'all',
  });

  const handleCloseNav = () => {
    dispatch(schedulesActions.setIsOpenDayOffNav(false));
  };

  const findIndexDate = (dates: Array<{
    date: string,
    type: string
  }>, date: Dayjs) => dates.findIndex((item) => {
    const compareDate = dayjs(item.date, 'YYYY-MM-DD').startOf('day');

    return dayjs(compareDate).isSame(dayjs(date));
  });

  const handleDates = (dates: Array<string>, date: Dayjs) => {
    const localDayOffData = [...dayOffData];
    const dateIndex = findIndexDate(localDayOffData, date);

    if (dateIndex >= 0) {
      localDayOffData.splice(dateIndex, 1);
    } else {
      localDayOffData.push({
        date: date.format('YYYY-MM-DD'),
        type: '',
      });
    }

    setDayOffData(localDayOffData);

    setDates(dates);
  };

  const handleRadioChange = (val: { text: string, value: string }, index: number) => {
    setDayOffData((prevState) => prevState.map((item, i) => (i === index ? { ...item, type: val.value } : item)));
  };

  const handleSubmit = async () => {
    const data = {
      days: dayOffData,
      note,
    };

    await mutateAsync(data);
    setTimeout(() => {
      setDates([]);
      setDayOffData([]);
    }, 500);
  };

  const isBtnDisabled = useMemo(() => !!dayOffData.find((item) => item.type === ''), [dayOffData]);

  const onCloseDrawer = () => {
    dispatch(commonActions.setToggleDrawer(false));
  };

  return (
    <BaseDrawer
      drawerProps={{
        anchor: 'right',
        width: isMobile ? 'auto' : 600,
        variant: 'persistent',
      }}
      open={isOpenDayOffNav}
      onCloseDrawer={onCloseDrawer}
    >
      <BaseDrawerTitle handleClose={handleCloseNav} title={t('timeoff')} />
      <Divider sx={{ borderStyle: 'dashed' }} />
      <FormProvider {...methods}>
        <Box
          paddingRight={isMobile ? 2 : 5}
          paddingLeft={isMobile ? 2 : 5}
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <Box width="100%" py={2}>
            <BaseLabel letterSpacing="-0.5px">{t('chooseDates')}</BaseLabel>
            <MultipleDaySelectionPicker width="100%" dates={dates} setDates={handleDates} />
          </Box>
          {dayOffData.length
            ? <Typography pt={2} variant="h5">{t('selectedDates')}</Typography>
            : null}
          <Box maxHeight={300} minHeight={100} pt={2}>
            {dayOffData.map((item, index) => (
              <Box
                key={item.date}
                {
                ...(!isMobile && {
                  display: 'flex',
                  justifyContent: 'space-between',
                })
              }
              >
                <Typography
                  color={theme.palette.primary.main}
                  {...(isMobile && {
                    textAlign: 'center',
                  })}
                  pt={1}
                >
                  {item.date}
                </Typography>
                <RadioGroup row>
                  <Stack spacing={1} direction="row">
                    {DAY_OFF_OPTIONS.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={
                          <Radio onChange={() => handleRadioChange(option, index)} />
                      }
                        label={option.text}
                      />
                    ))}
                  </Stack>
                </RadioGroup>
              </Box>
            ))}
          </Box>
          <Grid
            py={2}
            size={{
              lg: 12,
              md: 12,
              xs: 12,
              sm: 12,
            }}
          >
            <BaseLabel letterSpacing="-0.5px">{t('comment')}</BaseLabel>
            <TextField
              value={note}
              onChange={(event) => setNote(event.target.value)}
              multiline
              fullWidth
              rows={5}
            />
          </Grid>
          <Box my={2}>
            <LoadingButton
              onClick={handleSubmit}
              disabled={isBtnDisabled || !dayOffData.length}
              loading={isLoading}
              type="submit"
              size="large"
              variant="contained"
            >
              {t('sendRequest')}
            </LoadingButton>
          </Box>
        </Box>
      </FormProvider>
    </BaseDrawer>
  );
};

export {
  DayOffNav,
};
